import { createFeature, createReducer, on } from '@ngrx/store';

import { UserSession } from 'app/shared/models';
import { prependUniqueNumber } from 'app/shared/utils';
import { resetApiConfig } from 'app/state/api-config/api-config.actions';
import {
    initializeUserSessionStoreSuccess,
    loginSuccess,
    updatingSortActivityModeInPersonalUserPreferences,
    updatingUserRecentActivities,
    userSessionClosed,
    userSessionHistoryUpdated,
} from 'app/state/user-session/user-session.actions';
import { maxLengthRecentActivitiesInUserSession } from 'app/state/user-session/user-session.utils';

export const userSessionFeatureStoreKey = 'userSession';

export interface UserSessionState {
    userSession: UserSession | undefined;
    history: UserSession[];
}

export const initialState: UserSessionState = {
    userSession: undefined,
    history: [],
};

export const reducer = createReducer(
    initialState,

    on(initializeUserSessionStoreSuccess, (state, { userSession, history }) => ({
        ...state,
        userSession,
        history,
    })),
    on(loginSuccess, (state, { userSession }) => ({
        ...state,
        userSession,
    })),
    on(userSessionClosed, (state): UserSessionState => {
        return {
            ...state,
            userSession: undefined,
        };
    }),
    on(userSessionHistoryUpdated, (state, { userSession }) => {
        const newHistory = [
            userSession,
            ...state.history.filter(
                (s) => s.credentials.employeeNumber !== userSession.credentials.employeeNumber,
            ),
        ];
        return {
            ...state,
            history: newHistory,
        };
    }),
    on(updatingUserRecentActivities, (state, { activityNumber }) => {
        if (!state.userSession) return state;
        const currentPersonalUserPreferences = state.userSession.data;
        const recentActivityNumbers = prependUniqueNumber({
            numberToPrepend: activityNumber,
            currentArray: currentPersonalUserPreferences.lastActivityNumbers,
            maxLength: maxLengthRecentActivitiesInUserSession,
        });

        return {
            ...state,
            userSession: {
                ...state.userSession,
                data: { ...currentPersonalUserPreferences, lastActivityNumbers: recentActivityNumbers },
            },
        };
    }),
    on(updatingSortActivityModeInPersonalUserPreferences, (state, { sortActivityMode }) => {
        if (!state.userSession || sortActivityMode === state.userSession?.data.sortActivityMode) return state;
        const currentPersonalUserPreferences = state.userSession.data;
        return {
            ...state,
            userSession: {
                ...state.userSession,
                data: {
                    ...currentPersonalUserPreferences,
                    sortActivityMode,
                },
            },
        };
    }),
    on(resetApiConfig, () => initialState),
);

export const userSessionFeature = createFeature({
    name: userSessionFeatureStoreKey,
    reducer,
});
